import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { countryFlagLag, Language, SUPPORTED_LANGUAGES } from '@i18n';
import { useLanguage } from '@/context/LanguageContext';


/**
 * LanguageSwitcher - Dropdown component for switching application language
 *
 * Features:
 * - Dropdown select with supported languages
 * - Language preference persisted to localStorage
 * - Automatically updates all content when language changes
 * - Accessible with proper labels and ARIA attributes
 */
export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const { setLanguage, language } = useLanguage();
  const [announcement, setAnnouncement] = useState('');
  const [focusedLang, setFocusedLang] = useState<string | null>(null);

  const handleLanguageChange = (newLanguage: Language) => {
    i18n.changeLanguage(newLanguage);
    setLanguage(newLanguage);
    setAnnouncement(t(`langs-${newLanguage}-selected`));
  };

  const langs = useMemo(
    () => {
      return SUPPORTED_LANGUAGES.map( currLangKey => {
        return {
          val: currLangKey,
          img: !!countryFlagLag[currLangKey] ? countryFlagLag[currLangKey] : '',
          alt: `langs-${currLangKey}`,
        }
      })
    },
    []
  )

  return (
    <nav aria-label={t('language-switcher-label')}>
      <span role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </span>
      <ul className="flex list-none items-center gap-4 p-0 m-0 mode-totem:h-[53px] mode-totem:items-stretch">
        {
          langs.map( curr => {
            const isCurrent = curr.val === language;
            const Tag = isCurrent ? 'div' : 'button';
            return (
              <li key={curr.val} className="mode-totem:h-full">
                <Tag
                  {...(!isCurrent && {
                    onClick: () => handleLanguageChange(curr.val),
                  })}
                  aria-label={isCurrent ? `${t(curr.alt)}, ${t('language-current')}` : t(curr.alt)}
                  tabIndex={0}
                  aria-current={isCurrent ? 'true' : undefined}
                  onFocus={() => setFocusedLang(curr.val)}
                  onBlur={() => setFocusedLang(null)}
                  className={`p-0 border-0 bg-transparent transition-opacity min-h-11 min-w-10 mode-totem:w-full mode-totem:h-full mode-totem:min-h-0 ${isCurrent
                    ? `opacity-100 ring-offset-1 rounded-sm ${focusedLang === curr.val ? 'ring-4 ring-primary ring-offset-2' : 'ring-2 ring-primary'}`
                    : `hover:opacity-80 ${focusedLang === curr.val ? 'outline-2 outline-primary outline-offset-2' : ''}`
                  }`}
                >
                  <img
                    aria-hidden="true"
                    className="w-10 h-7 object-cover mode-totem:w-full mode-totem:h-full"
                    src={curr.img}
                    alt=""
                  />
                </Tag>
              </li>
            );
          })
        }
      </ul>
    </nav>
  );
}
